import {
  useItemsOpenEventsForOrganization,
  useItemsOpenEventsForUser,
} from 'shared/hooks/knowledge-dashboard.hooks';
import { useKnowledgeItems } from 'shared/hooks/knowledge-items.hooks';

export const MOST_VIEWED_SCOPES = {
  ORGANIZATION: 'organization',
  USER: 'user',
};

/**
 * Fetch at mount, never on scroll: RUM holds `loading_time` open until the network
 * goes quiet, so a viewport-gated request lands whenever the user scrolls and
 * drags the metric out with it.
 *
 * Both query hooks are called so hook order stays static; `enabled` picks which
 * one fetches. The open events only carry ids; the rows come back by id, in the
 * same order, minus the items this user cannot see.
 *
 * @param {typeof MOST_VIEWED_SCOPES[keyof typeof MOST_VIEWED_SCOPES]} scope
 * @returns {{ items: object[] | null }} `null` while loading, else the rows (empty on a failed request).
 * @example
 * const { items } = useMostViewedItems(MOST_VIEWED_SCOPES.USER);
 */
export function useMostViewedItems(scope) {
  const isOrganization = scope === MOST_VIEWED_SCOPES.ORGANIZATION;

  const organization = useItemsOpenEventsForOrganization({ enabled: isOrganization });
  const user = useItemsOpenEventsForUser({ enabled: !isOrganization });
  const { data: openEvents, isPending } = isOrganization ? organization : user;

  const ids = (openEvents ?? []).map(event => event.id);
  const rows = useKnowledgeItems(ids);

  // `null` is the widget's loading state, so a failed or paused (offline) request
  // must settle to `[]`.
  const isFetchingRows = ids.length > 0 && rows.isPending && rows.fetchStatus !== 'paused';
  if (isPending || isFetchingRows) return { items: null };

  return { items: rows.data ?? [] };
}
